import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

interface SubmitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isWememberz?: boolean;
  yearbookId?: number | null;
  validation?: { allowed: boolean; admin: string[] } | null;
}

function getCsrfToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta?.getAttribute('content') ?? '';
}

const SubmitDialog: React.FC<SubmitDialogProps> = ({ open, onOpenChange, isWememberz = false, yearbookId, validation }) => {
  const [checked, setChecked] = useState(false);

  const handleClose = () => {
    setChecked(false);
    onOpenChange(false);
  };

  const handleConfirm = () => {
    if (!checked) return;
    if (validation && !validation.allowed) return;
    if (isWememberz && yearbookId) {
      // POST + redirect via HTML form submission
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = `/carts/add_item?item_id=${yearbookId}&item_type=Yearbook`;
      const csrf = getCsrfToken();
      if (csrf) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'authenticity_token';
        input.value = csrf;
        form.appendChild(input);
      }
      document.body.appendChild(form);
      form.submit();
      return;
    }
    window.location.href = '/validation';
  };

  const title = isWememberz ? 'Ajouter au panier' : 'Envoi en vérification';
  const intro = isWememberz
    ? "Avant d'ajouter votre Yearbook au panier, vérifiez bien les points suivants :"
    : "Avant de confirmer l'envoi de votre Yearbook en vérification auprès des graphistes, vérifiez bien les points suivants :";

  const isAllowed = !validation || validation.allowed;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {validation === null || validation === undefined ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="animate-spin" size={16} />
              Vérification des droits…
            </div>
          ) : !isAllowed ? (
            <div className="space-y-3 rounded-md bg-destructive/10 p-4 text-sm">
              <p className="font-medium text-destructive">
                Seuls les administrateurs peuvent valider ce livre.
              </p>
              {validation.admin.length > 0 && (
                <div>
                  <p className="text-muted-foreground mb-1">Personnes autorisées :</p>
                  <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
                    {validation.admin.map((name) => (
                      <li key={name}>{name}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground leading-relaxed">{intro}</p>

              <ol className="list-decimal list-inside space-y-2 text-sm">
                <li>
                  Mes photos sont en HD ou à minima de bonnes qualités ?
                  <br />
                  <span className="text-muted-foreground text-xs ml-5">(pas floues ou pixellisées)</span>
                </li>
                <li>L'orthographe des textes a bien été vérifiée ?</li>
                <li>Tous les élèves sont présent·e·s dans le Trombinoscope ?</li>
              </ol>

              <div className="flex items-center gap-2 pt-2">
                <Checkbox
                  id="confirm-check"
                  checked={checked}
                  onCheckedChange={(v) => setChecked(v === true)}
                  disabled={!isAllowed}
                />
                <Label htmlFor="confirm-check" className="text-sm cursor-pointer">
                  J'ai bien vérifié mon fichier
                </Label>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={handleClose}>
            Je vérifie
          </Button>
          <Button disabled={!checked || !isAllowed} onClick={handleConfirm}>
            Je valide
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SubmitDialog;
